import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { CUSTOM_MASK } from '@helpers/custom-validator';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { ForeignPortModel, LocationOfGoodsModel } from '@data-layer/models/common';

@Component({
  selector: 'es-shipment-form-desktop',
  template: `
    <section class="info-shipment-main">
      <header class="info-shipment-main-header lg">
        <div class="info-shipment-main-header-text">
          <h3>{{ 'INFO.SHIP.TITLE' | translate }}</h3>

          <img src="assets/icons/icon_plain.svg" *ngIf="isAir" />

          <img src="assets/icons/icon_vessel.svg" *ngIf="isOcean" />

          <img src="assets/icons/icon_rail.svg" *ngIf="isRail" />

          <img src="assets/icons/icon_truck_sm.svg" *ngIf="isTruck" />
        </div>
      </header>
      <div class="info-shipment-main-body" *ngIf="!isTruck">
        <div class="form-row">
          <div *ngIf="!isAir && !isRail" class="col-6">
            <app-control-wrapper [control]="formGroup.get('vesselName')" [errors]="errors.vesselName" [isShowHelper]="false">
              <label for="vesselName" control-label>{{ 'LABELS.VESSEL_NAME' | translate }}</label>
              <input
                id="vesselName"
                class="form-control"
                type="text"
                name="vesselName"
                control-wrapper
                [formControl]="formGroup.get('vesselName')"
                [patterns]="CUSTOM_MASK"
                [mask]="'F{20}'"
                [maxMaskLength]="20"
                [appMaskErrorTooltip]="'MASK_ERROR.ONLY_ENGLISH_DIGITS_AND_SPECIAL'"
                [placeholder]="'FORM_PLACEHOLDER.ENTER' | translate"
                [dropSpecialCharacters]="false"
              />
            </app-control-wrapper>
          </div>
          <div [ngClass]="{ 'col-4': isAir, 'col-6': !isAir }">
            <app-control-wrapper [control]="formGroup.get('trip')" [errors]="errors.trip" [isShowHelper]="false">
              <label for="trip" control-label>
                {{ tripLabel | translate }}
              </label>
              <input
                id="trip"
                class="form-control"
                type="text"
                name="trip"
                control-wrapper
                [formControl]="formGroup.get('trip')"
                [patterns]="CUSTOM_MASK"
                [mask]="isAir ? '0{5}' : 'F{5}'"
                [appMaskErrorTooltip]="isAir ? 'MASK_ERROR.ONLY_DIGITS' : 'MASK_ERROR.ONLY_ENGLISH_DIGITS_AND_SPECIAL'"
                [maxLengthMessage]="isAir ? 'MASK_ERROR.MAX_DIGITS_LENGTH' : null"
                [maxMaskLength]="5"
                [placeholder]="'FORM_PLACEHOLDER.ENTER' | translate"
                [dropSpecialCharacters]="false"
              />
            </app-control-wrapper>
          </div>
        </div>
      </div>
    </section>

    <div class="from-to-section">
      <section class="info-shipment-main">
        <header class="info-shipment-main-header">
          <div class="info-shipment-main-header-text">
            <h3>
              {{ 'LABELS.FROM' | translate }}

              <app-hint-tooltip-icon [hint]="'ENTRY_SUMMARY.PLACE_OF_ORIGIN' | translate"></app-hint-tooltip-icon>
            </h3>
          </div>
        </header>
        <div class="info-shipment-main-body">
          <div class="form-row">
            <div class="col-12">
              <p class="text-hint">{{ 'ENTRY_SUMMARY.PLACE_OF_ORIGIN' | translate }}</p>
            </div>

            <ng-container>
              <div *ngIf="!isAir && !isTruck" class="col-12">
                <auto-select
                  [label]="'FORM_LABEL.FOREIGN_PORT'"
                  [bindLabel]="['code', 'portName', 'countryCode']"
                  [bindOption]="['code', 'portName', 'countryCode']"
                  [bindValue]="'id'"
                  [id]="'foreignPortOfLadingId'"
                  [change]="getForeignPorts"
                  [initializeItems]="initialForeignPorts"
                  [fc]="formGroup.get('foreignPortOfLadingId')"
                  [helper]="'FORM_HELPER.FOREIGN_PORT_OF_LADING'"
                  [placeholder]="'FORM_PLACEHOLDER.SEARCH_CODE_OR_NAME' | translate"
                  [errors]="errors.foreignPortOfLadingId"
                ></auto-select>
              </div>
              <div class="col-12 mb-3">
                <date-picker
                  [fc]="formGroup.get('exportDate')"
                  [helper]="exportHelper"
                  [errors]="errors.exportDate"
                  [label]="'FORM_LABEL.EXPORT_DATE'"
                  [id]="'exportDate'"
                  [name]="'exportDate'"
                  [maxDate]="exportMaxDate"
                  [placeholder]="'FORM_PLACEHOLDER.CHOOSE_DATE'"
                  [isWithIcon]="true"
                  [showCurrentDate]="true"
                ></date-picker>
              </div>
            </ng-container>
          </div>
        </div>
      </section>

      <div class="arrow-from-to">
        <span>
          <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="currentColor">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M0.500001 9L12.7 9L7.1 14.6L8.5 16L16.5 8L8.5 -6.99382e-07L7.1 1.4L12.7 7L0.500001 7L0.500001 9Z"
              fill="black"
            />
          </svg>
        </span>
      </div>

      <section class="info-shipment-main">
        <header class="info-shipment-main-header">
          <div class="info-shipment-main-header-text">
            <h3>
              {{ 'LABELS.TO' | translate }}

              <app-hint-tooltip-icon [hint]="'ENTRY_SUMMARY.PLACE_OF_FINAL_DESTINATION' | translate"></app-hint-tooltip-icon>
            </h3>
          </div>
        </header>
        <div class="info-shipment-main-body">
          <div class="form-row">
            <div class="col-12">
              <p class="text-hint">{{ 'ENTRY_SUMMARY.PLACE_OF_FINAL_DESTINATION' | translate }}</p>
            </div>

            <div class="col-12">
              <auto-select
                [label]="'FORM_LABEL.LOCATION_OF_GOODS'"
                [bindLabel]="['code', 'name']"
                [bindValue]="'code'"
                [id]="'locationCode'"
                [change]="searchLocations"
                [initializeItems]="initialLocations"
                [updateCountCharacter]="3"
                [fc]="formGroup.get('locationCode')"
                (update)="updateLocation.emit($event)"
                [helper]="locationHelper"
                [errors]="errors.locationCode"
              ></auto-select>
            </div>

            <div class="col-12">
              <auto-select
                [label]="'FORM_LABEL.PORT_OF_ENTRY'"
                [bindLabel]="['code', 'description']"
                [bindValue]="'code'"
                [id]="'portOfEntryCode'"
                [change]="getPorts"
                [initializeItems]="initialPorts"
                [dropdownPosition]="'top'"
                [errors]="errors.portOfEntryCode"
                [fc]="formGroup.get('portOfEntryCode')"
                [helper]="'FORM_HELPER.PORT_OF_ENTRY'"
                [placeholder]="'FORM_PLACEHOLDER.SEARCH_CODE_OR_NAME' | translate"
                [searchOnOpen]="true"
                [updateCountCharacter]="0"
              ></auto-select>
            </div>

            <div class="col-12">
              <auto-select
                [label]="'FORM_LABEL.STATE_OF_DESTINATION'"
                [bindLabel]="['code', 'description']"
                [bindOption]="['code', 'description']"
                [bindValue]="'code'"
                [change]="searchStates"
                [id]="'stateOfDestination'"
                [dropdownPosition]="'top'"
                [initializeItems]="initialStates"
                [errors]="errors.stateOfDestination"
                [fc]="formGroup.get('stateOfDestination')"
                [isShowHelper]="isTruck"
                [helper]="stateHelper"
              ></auto-select>
            </div>

            <div class="col-12">
              <date-picker
                [fc]="formGroup.get('arrivalDate')"
                [helper]="arrivalDateHelperStr"
                [errors]="errors.arrivalDate"
                [label]="'LABELS.ARRIVAL_DATE'"
                [id]="'arrivalDate'"
                [name]="'arrivalDate'"
                [minDate]="arrivalMinDate"
                [placeholder]="'FORM_PLACEHOLDER.CHOOSE_DATE'"
                [showCurrentDate]="true"
                [isWithIcon]="true"
              ></date-picker>
            </div>
          </div>
        </div>
      </section>
    </div>

    <section class="info-shipment-main" *ngIf="!isTruck">
      <header class="info-shipment-main-header">
        <div class="info-shipment-main-header-text">
          <h3>
            {{ 'ENTRY_SUMMARY.VIA' | translate }}

            <app-hint-tooltip-icon [hint]="'ENTRY_SUMMARY.VIA_DESC' | translate"></app-hint-tooltip-icon>
          </h3>
        </div>
      </header>
      <div class="info-shipment-main-body">
        <div class="form-row">
          <div class="col-12">
            <div class="custom-control custom-checkbox">
              <input
                type="checkbox"
                [formControl]="formGroup.get('differentPortEntry')"
                class="custom-control-input"
                id="differentPortEntry"
              />
              <label class="custom-control-label" for="differentPortEntry">{{ 'ENTRY_SUMMARY.VIA_DESC' | translate }}</label>
            </div>
          </div>
          <ng-container *ngIf="formGroup.get('differentPortEntry').value">
            <div class="col-4 mt-2">
              <auto-select
                [label]="'FORM_LABEL.PORT_OF_UNLADING_ARRIVAL'"
                [bindLabel]="['code', 'description']"
                [bindValue]="'code'"
                [change]="getViaPorts"
                [id]="'portOfUnladingCode'"
                [initializeItems]="initialPorts"
                [errors]="errors.portOfUnladingCode"
                [fc]="formGroup.get('portOfUnladingCode')"
                [helper]="'FORM_HELPER.PORT_OF_UNLADING_ARRIVAL'"
                [placeholder]="'FORM_PLACEHOLDER.SEARCH_CODE_OR_NAME' | translate"
              ></auto-select>
            </div>
          </ng-container>
        </div>
      </div>
    </section>
  `,
  styleUrls: ['./es-shipment-form-desktop.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsShipmentFormDesktopComponent {
  @Input() public isAir: boolean;
  @Input() public isOcean: boolean;
  @Input() public isRail: boolean;
  @Input() public isTruck: boolean;
  @Input() public formGroup: UntypedFormGroup;
  @Input() public errors: { [name: string]: IErrorForm };
  @Input() public getForeignPorts: Function;
  @Input() public getPorts: Function;
  @Input() public getViaPorts: Function;
  @Input() public searchLocations: Function;
  @Input() public searchStates: Function;
  @Input() public initialForeignPorts: ForeignPortModel[] = [];
  @Input() public initialPorts: ForeignPortModel[] = [];
  @Input() public initialStates: ForeignPortModel[] = [];
  @Input() public initialLocations: LocationOfGoodsModel[] = [];
  @Input() public exportMaxDate: string;
  @Input() public arrivalDateHelper: string;
  @Input() public arrivalMinDate: string;

  @Output() public updateLocation: EventEmitter<void> = new EventEmitter<void>();

  public CUSTOM_MASK = CUSTOM_MASK;

  constructor() {}

  public get tripLabel(): string {
    if (this.isAir) {
      return 'FORM_LABEL.FLIGHT_NUMBER';
    } else if (this.isOcean) {
      return 'LABELS.VOYAGE_NUMBER';
    } else if (this.isRail) {
      return 'FORM_LABEL.RAIL_TRIP_NUMBER';
    }
  }

  public get exportHelper(): string {
    if (this.isAir) {
      return 'FORM_HELPER.SHIPMENT_EXPORT_DATE_AIR';
    } else if (this.isOcean || this.isRail) {
      return 'FORM_HELPER.SHIPMENT_EXPORT_DATE_SHIP_DESKTOP';
    } else if (this.isTruck) {
      return 'FORM_HELPER.SHIPMENT_EXPORT_DATE_TRUCK';
    }
  }

  public get stateHelper(): string {
    if (this.isTruck) {
      return 'FORM_HELPER.SHIPMENT_STATE_TRUCK';
    } else {
      return '';
    }
  }

  public get locationHelper(): string {
    return this.isTruck ? 'FORM_HELPER.LOCATION_OF_GOODS_TRUCK' : 'FORM_HELPER.LOCATION_OF_GOODS';
  }

  public get arrivalDateHelperStr(): string {
    if (this.isTruck) {
      return 'FORM_HELPER.SHIPMENT_ARRIVAL_DATE_TRUCK';
    } else {
      return this.arrivalDateHelper;
    }
  }
}
